import { HttpEvent, HttpEventType } from '@angular/common/http';
import { OperatorFunction } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { LoadState } from '../enums/load-state.enum';
import { Download } from '../models/Download';

/**
 * The server answered a download with no body at all.
 *
 * Its own type rather than a bare `Error`, so the error handlers of the views can tell this apart:
 * nothing was decrypted and no credentials were involved here, which makes the messages about the
 * encryption or a wrong PIN — the fallbacks for an unrecognized error — plainly wrong.
 */
export class EmptyDownloadError extends Error {
  constructor() {
    super('The download completed without a response body.');
    this.name = 'EmptyDownloadError';
  }
}

/**
 * Maps Angular's raw `HttpEvent` stream onto the {@link Download} model, so callers can render
 * progress without knowing about `HttpEventType`.
 *
 * Requires the request to be issued with `observe: 'events'` and `reportProgress: true`; without
 * those, Angular emits only the response and no progress is reported.
 *
 * Events that carry no information for the caller (request sent, response headers, upload progress)
 * are dropped. A response without a body is turned into an error rather than being swallowed: a
 * stream that completes without ever emitting `done` would leave a progress indicator running
 * forever, which is precisely the failure mode this model exists to prevent.
 *
 * @return {OperatorFunction<HttpEvent<T>, Download<T>>} Operator emitting progress, then completion.
 */
export const toDownload = <T>(): OperatorFunction<HttpEvent<T>, Download<T>> => {
  return (source) =>
    source.pipe(
      map((event): Download<T> | null => {
        switch (event.type) {
          case HttpEventType.DownloadProgress:
            return { state: LoadState.Loading, loadedBytes: event.loaded, totalBytes: event.total };
          case HttpEventType.Response:
            if (event.body === null || event.body === undefined) {
              throw new EmptyDownloadError();
            }
            return { state: LoadState.Done, body: event.body };
          default:
            return null;
        }
      }),
      filter((download): download is Download<T> => download !== null),
    );
};
